import { Component, OnInit, Optional, inject } from '@angular/core';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { TransferService } from '../../../../../../core/services/transfer.service';
import { LocalStorageService } from '../../../../../../core/services/local-storage.service';
import { DeviceService } from '../../../../../../core/services/device.service';
import { StringHelper } from '../../../../../../core/helpers/strings.helper';

@Component({
    selector: 'app-beneficiary-bank-details',
    templateUrl: './beneficiary-bank-details.component.html',
    styleUrls: ['./beneficiary-bank-details.component.scss'],
})
export class BeneficiaryBankDetailsComponent implements OnInit {
    errorMessage: string = null;
    gettingBanks: boolean;
    banks = [];
    suggestedBanks = [];
    rolezPrefixes: any;
    kudaPrefixes: any;
    accountNumber: any;
    viewAllBanks = false;
    selectedBank: any;
    bankSearchTerm: string;
    transferAmount;
    BENEFICIARY_ERROR_BTN = 'Try Again';
    BENEFICIARY_ERROR = 'Beneficiary Not Found ';
    BENEFICIARY_ERROR_DES = 'We couldn’t find this person, ' + 'how about we check that account number and try again?  ';
    beneficiaryDetails = null;
    saveBeneficiary = false;
    deviceObj;
    otpResponse;
    contractCode;
    deadline;
    countDown;
    timer;
    countDownTimer;
    registerDevice = true;
    submitting = false;
    currentStepIndex = 2;
    filteredBanks = [];
    context: string;
    readonly nzModalData = inject(NZ_MODAL_DATA, { optional: true }) ?? {};

    constructor(
        @Optional() public modalService: NzModalRef,
        private transferService: TransferService,
        public localStorage: LocalStorageService,
        public deviceService: DeviceService,
    ) {}

    ngOnInit(): void {
        this.modalService.componentInstance = { ...this.modalService.componentInstance, ...this.nzModalData };
        this.deviceObj = this.deviceService.getDeviceObj();
        this.accountNumber = this.modalService.componentInstance.data.accountNumber;
        this.transferAmount = this.modalService.componentInstance.data.amount;
        this.contractCode = this.modalService.componentInstance.data.contractCode;
        this.context = this.modalService.componentInstance.data.context;
        this.getBanks();
    }

    setTimerValue() {
        this.countDown = true;
        // @ts-ignore
        this.countDownTimer = setInterval(() => {
            this.timer--;
            if (this.timer === 0) {
                this.countDown = false;
                clearInterval(this.countDownTimer);
                this.timer = 60;
            }
        }, 1000);
    }

    getBanks() {
        this.gettingBanks = true;
        this.errorMessage = null;
        this.transferService
            .getTransferConfigurations()
            .then((res) => {
                this.banks = res.banks;
                this.filteredBanks = res.banks;
                this.rolezPrefixes = res.rolezPrefixes;
                this.kudaPrefixes = res.kudaPrefixes;
                this.gettingBanks = false;
                this.loadSuggestedBanks(this.accountNumber);
            })
            .catch((err) => {
                this.errorMessage = StringHelper.getErrorMessageHttpErrorResponse(err);
                this.currentStepIndex = 1;
                this.gettingBanks = false;
            });
    }

    accountNameEnquiry() {
        this.errorMessage = null;
        this.submitting = true;
        const params = {
            bankId: this.selectedBank.id,
            accountNo: this.accountNumber,
            variant: this.context,
            amount: this.transferAmount,
            contractCode: this.contractCode,
        };
        this.transferService
            .accountNameEnquiry(params)
            .then((res) => {
                this.viewAllBanks = false;
                this.submitting = false;
                this.beneficiaryDetails = res;
                this.currentStepIndex = 4;
            })
            .catch((err) => {
                this.errorMessage = StringHelper.getErrorMessageHttpErrorResponse(err);
                this.currentStepIndex = 1;
                this.submitting = false;
            });
    }

    filterBankList(searchTerm?: string) {
        const query = searchTerm ? searchTerm.toLowerCase() : '';
        this.filteredBanks = this.banks.filter((bank) => bank.name.toLowerCase().includes(query));
    }

    saveBeneficiaryInformation() {
        this.submitting = true;
        this.beneficiaryDetails = {
            ...this.beneficiaryDetails,
            recipientBankCbnCode: this.selectedBank.cbnCode,
            bankName: this.selectedBank.name,
            saveBeneficiary: this.saveBeneficiary,
            nameEnquiryCacheReference: this.beneficiaryDetails.account.transactionReference,
            contractCode: this.contractCode,
        };
        this.modalService.componentInstance.callback(this.beneficiaryDetails);
    }

    noSelectedBank() {
        if (this.selectedBank) {
            return false;
        }
        return true;
    }

    loadSuggestedBanks(accountNo) {
        this.suggestedBanks = [];
        let lastDigit = this.getLastDigit(accountNo);
        if (!(this.banks && this.banks.length > 0)) {
            return;
        }
        this.banks.forEach((e) => {
            let currentBankNubanCode = e.cbnCode;
            let checkDigit = this.getCheckDigit(currentBankNubanCode, accountNo);

            if (currentBankNubanCode && checkDigit === lastDigit) {
                this.suggestedBanks.push(e);
            }
        });

        if (accountNo) {
            let firstChar = accountNo.charAt(0);
            if (this.rolezPrefixes.includes(firstChar)) {
                let search = StringHelper.getBankByCode('ROLEZ', this.suggestedBanks);
                let rolezSearch = StringHelper.getBankByCode('ROLEZ', this.banks);

                if (!search && rolezSearch) {
                    this.suggestedBanks.push(rolezSearch);
                }
            }

            if (this.kudaPrefixes.includes(firstChar)) {
                let search = StringHelper.getBankByCode('KUDA', this.suggestedBanks);
                let kudaSearch = StringHelper.getBankByCode('KUDA', this.banks);

                if (!search && kudaSearch) {
                    this.suggestedBanks.push(kudaSearch);
                }
            }
        }
    }

    selectBank(bank) {
        this.selectedBank = bank;
    }

    showAllBanks() {
        this.currentStepIndex = 3;
    }

    closeModal() {
        this.modalService.componentInstance.callback(null);
    }

    goBack() {
        this.currentStepIndex--;
    }

    private getLastDigit(accountNumber) {
        if (accountNumber && accountNumber.toString().length === 10) {
            return Number(accountNumber.toString().charAt(accountNumber.toString().length - 1));
        }
        return undefined;
    }

    private getCheckDigit(bankCode: string, accountNumber) {
        let _multipleArray = [3, 7, 3, 3, 7, 3, 3, 7, 3, 3, 7, 3]; // we could fetch this from a file
        let _concatNumber = bankCode + this.removeLastDigit(accountNumber);
        var _resultStep1 = 0;

        // step 1
        for (let i = 0; i < _concatNumber.length; i++) {
            _resultStep1 = _resultStep1 + Number(_concatNumber[i]) * _multipleArray[i];
        }

        let _resultStep2 = _resultStep1 % 10;
        //step 3 & step 4
        let _resultStep3 = 10 - _resultStep2;
        if (_resultStep3 === 10) {
            return 0;
        }

        return _resultStep3;
    }

    private removeLastDigit(accountNumber) {
        if (accountNumber && accountNumber.toString().length === 10) {
            return accountNumber.toString().slice(0, accountNumber.toString().length - 1);
        }

        return accountNumber;
    }
}
